import { useState, useEffect, useCallback, useRef } from 'react';
import socketService from '../services/socket';
import audioService from '../services/audio';
import toast from 'react-hot-toast';

/**
 * Hook for managing call training session
 */
export const useCallSession = () => {
  const [sessionId, setSessionId] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingMessage, setIsProcessingMessage] = useState(false);
  const [isFinalizingSpeech, setIsFinalizingSpeech] = useState(false);
  const [partialTranscript, setPartialTranscript] = useState('');
  
  // Mutex for preventing race conditions
  const recordingMutex = useRef(false);
  const processingMutex = useRef(false);
  const sessionIdRef = useRef(null);
  const isRecordingRef = useRef(false);
  const isActiveRef = useRef(false);
  const isAiThinkingRef = useRef(false);
  const isProcessingMessageRef = useRef(false);
  const isFinalizingSpeechRef = useRef(false);
  const recordingModeRef = useRef('auto');
  const startRecordingRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const playbackEpochRef = useRef(0);
  const activeResponseRef = useRef(null);
  const connectionReadyRef = useRef(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const [recordingMode, setRecordingModeState] = useState(() => {
    if (typeof window === 'undefined') return 'auto';
    return window.localStorage.getItem('recordingMode') || 'auto';
  });

  // New states for real-time analysis
  const [currentTip, setCurrentTip] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [objectivesProgress, setObjectivesProgress] = useState([]);
  const [finalAnalysis, setFinalAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Keep ref in sync with state for use in event handlers
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    isAiThinkingRef.current = isAiThinking;
  }, [isAiThinking]);

  useEffect(() => {
    isProcessingMessageRef.current = isProcessingMessage;
  }, [isProcessingMessage]);

  useEffect(() => {
    isFinalizingSpeechRef.current = isFinalizingSpeech;
  }, [isFinalizingSpeech]);

  useEffect(() => {
    recordingModeRef.current = recordingMode;
  }, [recordingMode]);

  const setRecordingMode = useCallback((mode) => {
    const nextMode = mode === 'hold' ? 'hold' : 'auto';
    recordingModeRef.current = nextMode;
    setRecordingModeState(nextMode);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('recordingMode', nextMode);
    }
  }, []);

  /**
   * Add message to conversation
   */
  const addMessage = useCallback((role, content) => {
    setMessages(prev => [...prev, {
      role,
      content,
      timestamp: new Date().toISOString(),
    }]);
  }, []);

  const resetSpeechState = useCallback(() => {
    playbackEpochRef.current++;
    activeResponseRef.current = null;
    window.clearTimeout(recordingTimerRef.current);
    recordingTimerRef.current = null;
    audioService.stopRecording();
    audioService.cancelCurrentPlayback('Call state reset');
    isRecordingRef.current = false;
    isAiThinkingRef.current = false;
    isProcessingMessageRef.current = false;
    isFinalizingSpeechRef.current = false;
    setIsRecording(false);
    setIsAiThinking(false);
    setIsProcessingMessage(false);
    setIsFinalizingSpeech(false);
    setPartialTranscript('');
  }, []);

  const scheduleRecording = useCallback(() => {
    window.clearTimeout(recordingTimerRef.current);
    const epoch = playbackEpochRef.current;
    if (recordingModeRef.current !== 'auto' || !isActiveRef.current) return;
    recordingTimerRef.current = window.setTimeout(() => {
      recordingTimerRef.current = null;
      if (epoch === playbackEpochRef.current && connectionReadyRef.current && isActiveRef.current) {
        startRecordingRef.current?.({ silent: true });
      }
    }, 350);
  }, []);

  /**
   * Initialize socket connection
   */
  useEffect(() => {
    socketService.connect();

    return () => {
      if (isActiveRef.current && sessionIdRef.current) {
        socketService.emit('call:end', { sessionId: sessionIdRef.current });
      }
      isActiveRef.current = false;
      resetSpeechState();
      audioService.cleanup();
      socketService.clearActiveSession();
      socketService.disconnect();
    };
  }, [resetSpeechState]);

  /**
   * Setup socket event listeners
   */
  useEffect(() => {
    const socket = socketService.socket;
    if (!socket) return;

    // Define handlers with stable references
    const handleCallStarted = (data) => {
      console.log('Call started:', data);
      resetSpeechState();
      connectionReadyRef.current = true;
      setIsReconnecting(false);
      sessionIdRef.current = data.sessionId;
      isProcessingMessageRef.current = true;
      setIsProcessingMessage(true);
      setMessages([]);
      setFinalAnalysis(null);
      setCurrentTip('');
      setMetrics(null);
      setObjectivesProgress([]);
      setSessionId(data.sessionId);
      setScenario(data.scenario);
      isActiveRef.current = true;
      setIsActive(true);
      socketService.setActiveSession(data.sessionId);
      toast.success('Звонок начался!');
    };

    const handleAiResponse = (data) => {
      if (!isActiveRef.current || (data.sessionId && data.sessionId !== sessionIdRef.current)) return;
      console.log('AI response:', data);
      addMessage('assistant', data.text);
      window.clearTimeout(recordingTimerRef.current);
      const epoch = ++playbackEpochRef.current;
      const sessionId = sessionIdRef.current;
      activeResponseRef.current = data.responseId || null;
      isAiThinkingRef.current = false;
      setIsAiThinking(false);
      isProcessingMessageRef.current = true;
      setIsProcessingMessage(true);
      const playbackComplete = (error = null) => {
        if (epoch !== playbackEpochRef.current || !isActiveRef.current || sessionId !== sessionIdRef.current) return;
        if (error?.code === 'PLAYBACK_CANCELED') return;
        if (error) toast.error('Не удалось воспроизвести голос ИИ. Диалог можно продолжить по тексту.');
        socketService.emit('audio:playback_complete', {
          sessionId, responseId: data.responseId,
          status: error ? 'failed' : 'completed',
          errorCode: error?.code || error?.name,
        });
      };

      // Play audio if available
      if (data.audio) {
        audioService.playAudio(data.audio, { durationMs: data.audioDurationMs })
          .then(() => playbackComplete())
          .catch(err => {
            console.error('Error playing audio:', err);
            playbackComplete(err);
          });
      } else {
        // No audio to play - notify immediately
        playbackComplete();
      }
    };

    const handleAiThinking = (data) => {
      if (!isActiveRef.current || (data.sessionId && data.sessionId !== sessionIdRef.current)) return;
      isAiThinkingRef.current = data.status;
      setIsAiThinking(data.status);
      if (data.status) {
        isFinalizingSpeechRef.current = false;
        setIsFinalizingSpeech(false);
        isProcessingMessageRef.current = true;
        setIsProcessingMessage(true);
      }
      
      // Automatically stop recording when AI starts thinking
      if (data.status && isRecordingRef.current) {
        audioService.stopRecording();
        isRecordingRef.current = false;
        setIsRecording(false);
      }
    };

    const handleSpeechRecognized = (data) => {
      console.log('Speech recognized:', data);
      addMessage('user', data.text);
      setPartialTranscript('');
    };

    const handleSpeechPartial = (data) => {
      setPartialTranscript(data.text);
    };

    const handleCallEnded = (data) => {
      if (data.sessionId && data.sessionId !== sessionIdRef.current) return;
      console.log('Call ended:', data);
      resetSpeechState();
      setIsReconnecting(false);
      isActiveRef.current = false;
      setIsActive(false);
      isRecordingRef.current = false;
      setIsRecording(false);
      isFinalizingSpeechRef.current = false;
      setIsFinalizingSpeech(false);
      setIsAnalyzing(false); // Stop analyzing animation
      audioService.cleanup();
      socketService.clearActiveSession();
      sessionIdRef.current = null;
      setSessionId(null);

      // Store final analysis
      if (data.analysis) {
        setFinalAnalysis(data.analysis);
      }

      if (data.analysisStatus === 'failed') {
        toast.error('Диалог сохранён, но AI-анализ временно недоступен. Ложная оценка не выставлена.');
      } else if (data.analysis) {
        toast.success('Звонок завершён, анализ готов!');
      } else {
        toast.success('Звонок завершён!');
      }
    };

    const handleError = (data) => {
      console.error('Socket error:', data);
      isFinalizingSpeechRef.current = false;
      setIsFinalizingSpeech(false);
      setIsAnalyzing(false);
      if (/Error (starting call|generating AI response|processing message|processing audio)|No active session/.test(data.message || '')) {
        resetSpeechState();
        if (data.message === 'Error starting call') {
          isActiveRef.current = false;
          setIsActive(false);
        }
      }
      // Don't show "No active session" toast if we're not in an active call
      // (can happen during reconnection race conditions)
      if (data.message === 'No active session' && !sessionIdRef.current) {
        return;
      }
      toast.error(data.message || 'Произошла ошибка');
    };

    // Session reconnection handlers
    const handleSessionReconnected = (data) => {
      console.log('Session reconnected:', data);
      resetSpeechState();
      connectionReadyRef.current = true;
      setIsReconnecting(false);
      sessionIdRef.current = data.sessionId;
      setSessionId(data.sessionId);
      setScenario(data.scenario);
      isActiveRef.current = true;
      setIsActive(true);
      isProcessingMessageRef.current = false;
      setIsProcessingMessage(false);

      // Restore conversation history
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: new Date().toISOString(),
        })));
      }

      toast.success('Соединение восстановлено!');
      scheduleRecording();
    };

    const handleSessionLost = (data) => {
      console.log('Session lost:', data);
      resetSpeechState();
      connectionReadyRef.current = false;
      setIsReconnecting(false);
      sessionIdRef.current = null;
      socketService.clearActiveSession();
      setSessionId(null);
      setScenario(null);
      setMessages([]);
      isActiveRef.current = false;
      setIsActive(false);
      isRecordingRef.current = false;
      setIsRecording(false);
      isProcessingMessageRef.current = false;
      setIsProcessingMessage(false);
      isFinalizingSpeechRef.current = false;
      setIsFinalizingSpeech(false);
      audioService.cleanup();
      toast.error('Соединение потеряно. Начните звонок заново.');
    };

    const handleDisconnect = () => {
      connectionReadyRef.current = false;
      resetSpeechState();
      if (isActiveRef.current) setIsReconnecting(true);
    };

    // New handlers for real-time analysis
    const handleCoachingTip = (data) => {
      console.log('Coaching tip:', data);
      setCurrentTip(data.tip);
    };

    const handleMetricsUpdate = (data) => {
      console.log('Metrics update:', data);
      setMetrics(data.metrics);
    };

    const handleObjectivesProgress = (data) => {
      console.log('Objectives progress:', data);
      setObjectivesProgress(data.objectives);
    };

    const handleMessageProcessingComplete = (data) => {
      if (data.sessionId && data.sessionId !== sessionIdRef.current) return;
      if (data.responseId && activeResponseRef.current && data.responseId !== activeResponseRef.current) return;
      console.log('Message processing complete:', data);
      activeResponseRef.current = null;
      isAiThinkingRef.current = false;
      setIsAiThinking(false);
      isProcessingMessageRef.current = false;
      setIsProcessingMessage(false);
      isFinalizingSpeechRef.current = false;
      setIsFinalizingSpeech(false);

      scheduleRecording();
    };

    const handleAudioFinalizeComplete = () => {
      isFinalizingSpeechRef.current = false;
      setIsFinalizingSpeech(false);
    };

    // Register handlers
    socket.on('call:started', handleCallStarted);
    socket.on('ai:response', handleAiResponse);
    socket.on('ai:thinking', handleAiThinking);
    socket.on('speech:recognized', handleSpeechRecognized);
    socket.on('speech:partial', handleSpeechPartial);
    socket.on('call:ended', handleCallEnded);
    socket.on('error', handleError);
    socket.on('coaching:tip', handleCoachingTip);
    socket.on('metrics:update', handleMetricsUpdate);
    socket.on('objectives:progress', handleObjectivesProgress);
    socket.on('message:processing_complete', handleMessageProcessingComplete);
    socket.on('audio:finalize_complete', handleAudioFinalizeComplete);
    socket.on('session:reconnected', handleSessionReconnected);
    socket.on('session:lost', handleSessionLost);
    socket.on('disconnect', handleDisconnect);

    // Cleanup with specific handler references
    return () => {
      socket.off('call:started', handleCallStarted);
      socket.off('ai:response', handleAiResponse);
      socket.off('ai:thinking', handleAiThinking);
      socket.off('speech:recognized', handleSpeechRecognized);
      socket.off('speech:partial', handleSpeechPartial);
      socket.off('call:ended', handleCallEnded);
      socket.off('error', handleError);
      socket.off('coaching:tip', handleCoachingTip);
      socket.off('metrics:update', handleMetricsUpdate);
      socket.off('objectives:progress', handleObjectivesProgress);
      socket.off('message:processing_complete', handleMessageProcessingComplete);
      socket.off('audio:finalize_complete', handleAudioFinalizeComplete);
      socket.off('session:reconnected', handleSessionReconnected);
      socket.off('session:lost', handleSessionLost);
      socket.off('disconnect', handleDisconnect);
    };
  }, [addMessage, resetSpeechState, scheduleRecording]);

  /**
   * Start call session
   */
  const startCall = useCallback(async (scenarioId) => {
    // Prevent multiple simultaneous call starts
    if (processingMutex.current) {
      console.warn('Call start already in progress');
      return;
    }
    
    processingMutex.current = true;
    
    try {
      // Request microphone access
      const hasAccess = await audioService.requestMicrophoneAccess();
      if (!hasAccess) {
        toast.error('Требуется доступ к микрофону');
        return;
      }

      // Start call via socket
      socketService.emit('call:start', { scenarioId });
    } catch (error) {
      console.error('Error starting call:', error);
      toast.error('Ошибка при запуске звонка');
    } finally {
      processingMutex.current = false;
    }
  }, []);

  /**
   * End call session
   */
  const endCall = useCallback(() => {
    if (!sessionId) return;

    // Start analyzing animation
    setIsAnalyzing(true);
    isActiveRef.current = false;
    resetSpeechState();
    socketService.clearActiveSession();
    setIsReconnecting(false);
    socketService.emit('call:end', { sessionId });
    audioService.cleanup();
    isActiveRef.current = false;
    setIsActive(false);
    isRecordingRef.current = false;
    setIsRecording(false);
    isFinalizingSpeechRef.current = false;
    setIsFinalizingSpeech(false);
    // Keep currentTip, metrics, objectivesProgress, and finalAnalysis for display
  }, [sessionId, resetSpeechState]);

  /**
   * Send text message
   */
  const sendMessage = useCallback((message) => {
    if (!sessionId || !message.trim()) return;
    
    // Prevent multiple simultaneous message sends
    if (processingMutex.current) {
      console.warn('Message send already in progress');
      return;
    }
    
    processingMutex.current = true;
    
    try {
      socketService.emit('message:send', { message });
      addMessage('user', message);
    } finally {
      processingMutex.current = false;
    }
  }, [sessionId, addMessage]);

  /**
   * Start voice recording with mutex protection
   */
  const startRecording = useCallback(async ({ silent = false } = {}) => {
    if (!sessionIdRef.current) return;
    if (!isActiveRef.current || !connectionReadyRef.current) return;
    if (isRecordingRef.current) return;
    
    // Prevent multiple simultaneous recording state changes
    if (recordingMutex.current) {
      console.warn('Recording start already in progress');
      return;
    }
    
    recordingMutex.current = true;
    
    try {
      // Block recording during message processing or when AI is thinking
      if (isProcessingMessageRef.current || isAiThinkingRef.current || isFinalizingSpeechRef.current) {
        if (!silent) {
          toast.error('Нельзя начать запись во время обработки сообщения');
        }
        return;
      }
      
      const epoch = playbackEpochRef.current;
      await audioService.startRecording((audioChunk) => {
        if (connectionReadyRef.current && isActiveRef.current) socketService.emit('audio:stream', { audioChunk });
      });
      if (epoch !== playbackEpochRef.current || !isActiveRef.current || !connectionReadyRef.current) {
        audioService.stopRecording();
        return;
      }
      isRecordingRef.current = true;
      setIsRecording(true);
    } catch (error) {
      console.error('Could not start microphone:', error);
      if (!silent) toast.error('Не удалось включить микрофон. Проверьте доступ к нему.');
    } finally {
      // Release mutex after a short delay to prevent rapid successive calls
      setTimeout(() => {
        recordingMutex.current = false;
      }, 100);
    }
  }, []);

  startRecordingRef.current = startRecording;

  /**
   * Stop voice recording and optionally ask the backend to finalize speech now
   */
  const stopRecording = useCallback(({ finalize = true } = {}) => {
    if (!isRecordingRef.current) return;

    audioService.stopRecording();
    isRecordingRef.current = false;
    setIsRecording(false);

    if (finalize && sessionIdRef.current) {
      isFinalizingSpeechRef.current = true;
      setIsFinalizingSpeech(true);
      socketService.emit('audio:finalize');
    }
  }, []);

  /**
   * Toggle voice recording with mutex protection
   */
  const toggleRecording = useCallback(async () => {
    if (isRecordingRef.current) {
      stopRecording({ finalize: false });
      return;
    }

    await startRecording();
  }, [startRecording, stopRecording]);

  return {
    sessionId,
    scenario,
    messages,
    isActive,
    isAiThinking,
    isRecording,
    partialTranscript,
    currentTip,
    metrics,
    objectivesProgress,
    finalAnalysis,
    isAnalyzing,
    isReconnecting,
    isProcessingMessage,
    isFinalizingSpeech,
    recordingMode,
    startCall,
    endCall,
    sendMessage,
    setRecordingMode,
    startRecording,
    stopRecording,
    toggleRecording,
  };
};
